import { useState } from "react";
import { Link } from "wouter";
import { Pencil } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { useOrigination } from "@/hooks/useOrigination";
import { useSuggestions } from "@/hooks/useSuggestions";
import { SourceControl } from "./SourceControl";
import { SuggestionRow } from "./SuggestionRow";
import { describeOrigination } from "./describeOrigination";
import type { OriginationInput, OriginationView } from "@shared/originationConstants";

/**
 * The rail card. Three shapes in one slot (person, website, outbound), plus a
 * pending-suggestion state and a blank state that IS the control. Same
 * component on prospect and client pages, so the six engagements in the book
 * get attributed by hand in one sitting. `compact` is the record-page rail
 * form: one `kind · primary` line with an edit pencil, or "Source Add" when unset, and no heading row.
 */
export function SourceCard({ companyId, editable = true, compact = false }: { companyId: string; editable?: boolean; compact?: boolean }) {
  const { user } = useAuth();
  const { data: origination, isLoading, save } = useOrigination(companyId);
  const { data: suggestions = [], accept, dismiss } = useSuggestions({ companyId });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<OriginationInput | null>(null);

  const startEdit = () => {
    setDraft(origination ? { ...origination } : null);
    setEditing(true);
  };
  const commit = () => {
    if (!draft) return;
    save.mutate(draft, {
      onSuccess: () => { setEditing(false); toast.success("Source saved"); },
      onError: (e: any) => toast.error(e.message),
    });
  };

  const inbound = suggestions.filter((s) => s.direction === "in");

  return (
    <div className={compact ? "" : "border rounded-lg p-4 border-l-4 border-l-amber-500/70"}>
      {!compact && (
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Source</h3>
          {editable && origination && !editing && (
            <button className="text-xs text-primary-text hover:underline" onClick={startEdit}>Change source</button>
          )}
        </div>
      )}

      {inbound.length > 0 && !editing && (
        <div className="space-y-2 mb-3">
          {inbound.map((s) => (
            <SuggestionRow
              key={s.id} s={s} showCompany={false} pending={accept.isPending || dismiss.isPending}
              onAccept={() => accept.mutate(s.id, { onSuccess: () => toast.success("Attributed") })}
              onDismiss={() => dismiss.mutate(s.id)}
            />
          ))}
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : editing || (!origination && editable && !compact) ? (
        <div className="space-y-3">
          <SourceControl value={draft} onChange={setDraft} currentUserId={user?.id} />
          <div className="flex gap-2 justify-end">
            {(origination || compact) && <Button size="sm" variant="outline" onClick={() => setEditing(false)}>Cancel</Button>}
            <Button size="sm" onClick={commit} disabled={save.isPending || !draft}>Save</Button>
          </div>
        </div>
      ) : !origination ? (
        compact && editable ? (
          <div className="flex items-center gap-1.5 text-[13px]">
            <span className="text-muted-foreground">Source</span>
            <button type="button" onClick={startEdit} className="font-semibold text-primary-text hover:underline">Add</button>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground italic">Source not recorded.</p>
        )
      ) : compact ? (
        <CompactSource o={origination} onEdit={editable && !editing ? startEdit : undefined} />
      ) : (
        <SourceSummary o={origination} />
      )}
    </div>
  );
}

/** One line for the rail: kind and who, the rest in the tooltip, then the edit pencil. */
function CompactSource({ o, onEdit }: { o: OriginationView; onEdit?: () => void }) {
  const d = describeOrigination(o);
  const when = o.occurredOn ? new Date(o.occurredOn + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : null;
  const tip = [d.secondary, when, o.note ? `\u201c${o.note}\u201d` : null].filter(Boolean).join(" \u00b7 ");
  return (
    <div className="flex items-center gap-1.5 text-[13px]" title={tip || undefined}>
      <span className="min-w-0 truncate">
        <span className="text-muted-foreground">{d.kind} · </span>
        {d.href ? <Link href={d.href} className="font-medium hover:underline">{d.primary}</Link> : <span className="font-medium">{d.primary}</span>}
      </span>
      {onEdit && (
        <button type="button" className="shrink-0 text-muted-foreground hover:text-foreground" aria-label="Change source" onClick={onEdit}>
          <Pencil className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

function SourceSummary({ o }: { o: OriginationView }) {
  const d = describeOrigination(o);
  const when = o.occurredOn ? new Date(o.occurredOn + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : null;
  return (
    <div className="text-sm space-y-1">
      <div>
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground mr-2">{d.kind}</span>
        {d.href ? <Link href={d.href} className="font-medium hover:underline">{d.primary}</Link> : <span className="font-medium">{d.primary}</span>}
      </div>
      {(d.secondary || when) && <div className="text-xs text-muted-foreground">{[d.secondary, when].filter(Boolean).join(" · ")}</div>}
      {o.note && <p className="text-xs text-muted-foreground italic mt-1">“{o.note}”</p>}
      {o.detectedFromEmailId && <p className="text-[11px] text-muted-foreground">Detected from email, confirmed by a person.</p>}
    </div>
  );
}
